import { Preferences } from "@capacitor/preferences";
import { useSyncExternalStore } from "react";

const STORAGE_KEY = "superhost.haptics.v1";

/**
 * Device-scoped, unlike the other preference modules here, which key by user id
 * so a shared phone doesn't leak one person's layout to the next. Haptics are a
 * property of the device in someone's hand rather than of the account signed
 * into it: a housekeeper who silences a shared handset wants it silent for
 * whoever picks it up next, not silent only while they are the one logged in.
 *
 * It also has to be readable synchronously — see `read` below — and a per-user
 * key cannot be, because the session resolves asynchronously and haptics fire
 * from plain event handlers.
 */
let enabled = true;

const subscribers = new Set<() => void>();

function publish(): void {
  for (const notify of subscribers) {
    notify();
  }
}

/**
 * Load the stored choice into the cache. Started at module load and awaited by
 * nobody: until it resolves the default (on) applies, which is the same thing
 * the user would feel on a fresh install.
 */
async function hydrate(): Promise<void> {
  try {
    const { value } = await Preferences.get({ key: STORAGE_KEY });
    if (value === null) {
      return;
    }
    enabled = value !== "false";
    publish();
  } catch (err) {
    console.warn("[haptics] failed to read the stored preference:", err);
  }
}

void hydrate();

/**
 * The current setting, read synchronously.
 *
 * `triggerHaptic` is called from ordinary event handlers and cannot await, so
 * the value has to be available without a promise. Hence the module-level
 * cache: reads are always cheap and never block a tap.
 */
export function hapticsEnabled(): boolean {
  return enabled;
}

/** Persist a new choice and apply it immediately. */
export function setHapticsEnabled(next: boolean): void {
  enabled = next;
  publish();
  Preferences.set({ key: STORAGE_KEY, value: String(next) }).catch((err) => {
    console.warn("[haptics] failed to persist the preference:", err);
  });
}

function subscribe(notify: () => void): () => void {
  subscribers.add(notify);
  return () => {
    subscribers.delete(notify);
  };
}

/** The setting plus its setter, for the Account screen's toggle. */
export function useHapticsPreference(): {
  hapticsOn: boolean;
  setHapticsOn: (next: boolean) => void;
} {
  const hapticsOn = useSyncExternalStore(subscribe, hapticsEnabled, () => true);
  return { hapticsOn, setHapticsOn: setHapticsEnabled };
}

/** Reset the cache between tests. Not used by app code. */
export function resetHapticsPreferenceForTest(): void {
  enabled = true;
  subscribers.clear();
}
